import {
  Row,
} from '@tanstack/react-table';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import Tooltip from 'core/components/Tooltip';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import DeleteIcon from 'core/icons/delete';
import PinIcon from 'core/icons/pin';
import {
  PayerEntryConfigDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import React, {
  MouseEvent,
} from 'react';
import {
  useParams,
} from 'react-router-dom';
import {
  getAddressString,
} from 'utils/string';
import {
  useDeletePayerConfig,
  useUpsertElectronicPayerConfig,
} from 'views/EMR/Settings/hooks';

type PayerNameCellProps = {
  row: Row<PayerEntryConfigDTO>;
};

function PayerNameCell({ row }: PayerNameCellProps) {
  const { mutateAsync: updateConfig, isPending: isLoading } = useUpsertElectronicPayerConfig();
  const { type = 'electronic' } = useParams();
  const isElectronic = type === 'electronic';
  const notify = useNotify();
  const payerLabel = isElectronic ? 'Payer Configuration' : 'Payer';

  const togglePin = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.currentTarget?.blur?.();
    await updateConfig({
      ...row.original,
      isPinned: !row.original.isPinned,
    });
    notify({
      message: `Payer${isElectronic ? ' Configuration' : ''} has been updated.`,
      variant: 'success',
    });
    settingsEventChannel.emit('onPayerConfigChange');
  };

  return (
    <div className="flex gap-2 items-center">
      <Tooltip
        title={row.original?.isPinned ? `Un-Pin ${payerLabel}` : `Pin ${payerLabel}`}
        disableFocusListener={false}
      >
        <IconButton
          size="small"
          color="info"
          className={clsx({
            '!text-blue-500': row.original?.isPinned,
            '!text-gray-400': !row.original?.isPinned,
          })}
          disabled={isLoading}
          onClick={togglePin}
        >
          {isLoading
            ? <Spinner />
            : (
              <PinIcon
                className={clsx('!w-4 !h-4 !text-inherit', {
                  'rotate-45': !row.original?.isPinned,
                })}
              />
            )}
        </IconButton>
      </Tooltip>
      <strong className="font-semibold">{row.original?.payerName}</strong>
    </div>
  );
}

type PayerActionCellProps = {
  row: {
    original: PayerEntryConfigDTO;
  };
};

function PayerActionCell({ row }: PayerActionCellProps) {
  const { type = 'electronic' } = useParams();
  const isElectronic = type === 'electronic';
  const { confirm, closeConfirm } = useConfirmDialog();
  const notify = useNotify();
  const { payerName, irremovableReason } = row.original;
  const { mutateAsync: deletePayerConfig } = useDeletePayerConfig();

  const handleDelete = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    confirm({
      content: (
        <p>
          Are you sure you want to delete payer
          {isElectronic ? ' configuration ' : ' '}
          <strong className="font-semibold">{payerName}</strong>
          ?
        </p>
      ),
      onConfirm: async () => {
        await deletePayerConfig(row?.original);
        notify({
          message: `Payer${isElectronic ? ' Configuration' : ''} has been deleted.`,
          variant: 'success',
        });
        settingsEventChannel.emit('onPayerConfigChange');
        closeConfirm();
      },
      title: isElectronic ? 'Delete Payer Configuration' : 'Delete Payer',
      confirmText: 'Confirm',
    });
  };

  return (
    <Tooltip title={irremovableReason ?? `Delete Payer${isElectronic ? ' Configuration' : ''}`}>
      <IconButton
        onClick={handleDelete}
        size="small"
        color="error"
        className={clsx('disabled:opacity-50', {
          '!cursor-not-allowed !pointer-events-auto': !isNil(irremovableReason),
        })}
        disabled={!isNil(irremovableReason)}
      >
        <DeleteIcon className="!w-5 !h-5 text-gray-500" />
      </IconButton>
    </Tooltip>
  );
}

const payerNameColumn: ColumnDef<PayerEntryConfigDTO> = {
  id: 'payer',
  accessorKey: 'payerName',
  columnTitle: 'Name',
  meta: { autoSized: true },
  cell: ({ row }) => <PayerNameCell row={row} />,
};

const addressColumn: ColumnDef<PayerEntryConfigDTO> = {
  id: 'address',
  accessorKey: 'address',
  columnTitle: 'Address',
  meta: { autoSized: true },
  cell: ({ row }) => (
    <div className="flex flex-col gap-1">
      {getAddressString(row.original?.payerEntry?.address)}
    </div>
  ),
};

const insuranceTypeColumn: ColumnDef<PayerEntryConfigDTO> = {
  id: 'insuranceType',
  accessorKey: 'payerEntry.insuranceType',
  columnTitle: 'Type',
  size: 250,
  className: 'font-semibold',
};

const payerIdColumn: ColumnDef<PayerEntryConfigDTO> = {
  id: 'payerId',
  accessorKey: 'payerId',
  columnTitle: 'Payer ID',
  meta: { autoSized: true },
  className: 'font-semibold',
  cell: ({ row }) => row.original?.payerId ?? '--',
};

const actionColumn: ColumnDef<PayerEntryConfigDTO> = {
  id: 'actions',
  header: '',
  size: 40,
  cell: ({ row }) => <PayerActionCell row={row} />,
};

export const electronicPayerColumns = [
  payerNameColumn,
  payerIdColumn,
  actionColumn,
];

export const paperPayerColumns = [
  payerNameColumn,
  insuranceTypeColumn,
  payerIdColumn,
  addressColumn,
  actionColumn,
];
